import React, { useState, useEffect } from 'react';
import { Grid, Card, CardContent, Typography, Box, Button, Stack, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import StatsCard from '../../components/StatsCard';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { getRevenueReport, exportRevenueReport } from '../../api/endpoints/reports';
import { downloadBlob } from '../../utils/exportHelpers';

const RevenueReport = () => {
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      const res = await getRevenueReport(params);
      setReport(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load revenue report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      await downloadBlob(exportRevenueReport(params), `revenue-report-${Date.now()}.csv`);
      toast.success('Revenue report exported');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to export report');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const daily = report?.daily || [];
  const byCategory = report?.byCategory || [];
  const totalRevenue = report?.totalRevenue || 0;
  const totalTransactions = byCategory.reduce((s, i) => s + (i.count || 0), 0);

  return (
    <Box>
      <PageHeader title="Revenue Report" subtitle="Track and analyze platform revenue">
        <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport}>Export Report</Button>
      </PageHeader>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatsCard label="Total Revenue" value={formatCurrency(totalRevenue)} color="#2D1B69" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatsCard label="Total Transactions" value={totalTransactions} color="#7C5CBF" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatsCard label="Categories" value={byCategory.length} color="#FFD700" />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField type="date" size="small" label="Start Date" InputLabelProps={{ shrink: true }} value={dateRange.start} onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))} />
        <TextField type="date" size="small" label="End Date" InputLabelProps={{ shrink: true }} value={dateRange.end} onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))} />
        <Button variant="contained" size="small" onClick={fetchReport}>Apply</Button>
      </Stack>

      {byCategory.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Revenue Breakdown by Category</Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">% of Total</TableCell>
                    <TableCell align="right">Transactions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byCategory.map((item) => (
                    <TableRow key={item._id || item.type} hover>
                      <TableCell><Typography fontWeight={500}>{item._id || item.type}</Typography></TableCell>
                      <TableCell align="right">{formatCurrency(item.total || item.amount || 0)}</TableCell>
                      <TableCell align="right">{totalRevenue ? ((item.total || item.amount || 0) / totalRevenue * 100).toFixed(1) : 0}%</TableCell>
                      <TableCell align="right">{item.count || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default RevenueReport;